// @ts-nocheck - Temporary fix for webhook service types
interface WebhookEndpoint {
  id: string;
  url: string;
  events: WebhookEvent[];
  secret: string;
  isActive: boolean;
  retryPolicy: {
    maxRetries: number;
    retryDelay: number; // milliseconds
    backoffMultiplier: number;
  };
  headers?: { [key: string]: string };
  createdAt: Date;
  lastTriggered?: Date;
  successCount: number;
  failureCount: number;
}

type WebhookEvent = 
  | 'job.created'
  | 'job.updated'
  | 'job.completed'
  | 'job.cancelled'
  | 'quote.submitted'
  | 'quote.accepted'
  | 'quote.rejected'
  | 'booking.confirmed'
  | 'booking.cancelled'
  | 'payment.completed'
  | 'payment.failed'
  | 'provider.verified'
  | 'provider.suspended'
  | 'review.submitted'
  | 'dispute.created'
  | 'dispute.resolved';

interface WebhookPayload {
  id: string;
  event: WebhookEvent;
  timestamp: Date;
  data: any;
  signature: string;
}

interface WebhookDelivery {
  id: string;
  endpointId: string;
  event: WebhookEvent;
  payload: WebhookPayload;
  status: 'PENDING' | 'DELIVERED' | 'FAILED' | 'RETRYING';
  attempts: number;
  lastAttempt?: Date;
  nextRetry?: Date;
  response?: {
    statusCode: number;
    body: string;
    headers: { [key: string]: string };
  };
  error?: string;
  createdAt: Date;
}

class WebhookService {
  private readonly STORAGE_KEY = 'webhooks';
  private deliveryQueue: WebhookDelivery[] = [];
  private isProcessing = false;

  constructor() {
    this.startDeliveryProcessor();
  }

  // Webhook Endpoint Management
  async registerWebhook(
    url: string,
    events: WebhookEvent[],
    secret?: string,
    headers?: { [key: string]: string }
  ): Promise<WebhookEndpoint> {
    const webhook: WebhookEndpoint = {
      id: `webhook_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      url,
      events,
      secret: secret || this.generateSecret(),
      isActive: true,
      retryPolicy: {
        maxRetries: 3,
        retryDelay: 1000,
        backoffMultiplier: 2
      },
      headers,
      createdAt: new Date(),
      successCount: 0,
      failureCount: 0
    };

    const webhooks = this.getWebhooks();
    webhooks.push(webhook);
    this.saveWebhooks(webhooks);

    return webhook;
  }

  async updateWebhook(
    webhookId: string,
    updates: Partial<Pick<WebhookEndpoint, 'url' | 'events' | 'isActive' | 'headers' | 'retryPolicy'>>
  ): Promise<WebhookEndpoint | null> {
    const webhooks = this.getWebhooks();
    const index = webhooks.findIndex(w => w.id === webhookId);
    
    if (index === -1) return null;

    webhooks[index] = { ...webhooks[index], ...updates };
    this.saveWebhooks(webhooks);

    return webhooks[index];
  }

  async deleteWebhook(webhookId: string): Promise<boolean> {
    const webhooks = this.getWebhooks();
    const filteredWebhooks = webhooks.filter(w => w.id !== webhookId);
    
    if (filteredWebhooks.length === webhooks.length) return false;

    this.saveWebhooks(filteredWebhooks);
    return true;
  }

  getWebhooks(): WebhookEndpoint[] {
    const stored = localStorage.getItem(`${this.STORAGE_KEY}_endpoints`);
    return stored ? JSON.parse(stored) : [];
  }

  getWebhook(webhookId: string): WebhookEndpoint | null {
    const webhooks = this.getWebhooks();
    return webhooks.find(w => w.id === webhookId) || null;
  }

  // Event Triggering
  async triggerEvent(event: WebhookEvent, data: any): Promise<void> {
    const webhooks = this.getWebhooks().filter(w => 
      w.isActive && w.events.includes(event)
    );

    if (webhooks.length === 0) return;

    const payload: WebhookPayload = {
      id: `payload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      event,
      timestamp: new Date(),
      data,
      signature: ''
    };

    for (const webhook of webhooks) {
      payload.signature = this.generateSignature(payload, webhook.secret);
      
      const delivery: WebhookDelivery = {
        id: `delivery_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        endpointId: webhook.id,
        event,
        payload,
        status: 'PENDING',
        attempts: 0,
        createdAt: new Date()
      };

      this.deliveryQueue.push(delivery);
      this.saveDelivery(delivery);
    }

    this.processDeliveryQueue();
  }

  // Delivery Processing
  private async processDeliveryQueue(): Promise<void> {
    if (this.isProcessing) return;
    this.isProcessing = true;

    try {
      const pendingDeliveries = this.deliveryQueue.filter(d => 
        d.status === 'PENDING' || 
        (d.status === 'RETRYING' && d.nextRetry && new Date() >= d.nextRetry)
      );

      for (const delivery of pendingDeliveries) {
        await this.attemptDelivery(delivery);
      }
    } finally {
      this.isProcessing = false;
    }
  }

  private async attemptDelivery(delivery: WebhookDelivery): Promise<void> {
    const webhook = this.getWebhook(delivery.endpointId);
    if (!webhook) {
      delivery.status = 'FAILED';
      delivery.error = 'Webhook endpoint not found';
      this.updateDelivery(delivery);
      return;
    }

    delivery.attempts++;
    delivery.lastAttempt = new Date();

    try {
      const response = await this.sendWebhook(webhook, delivery.payload);
      
      if (response.ok) {
        delivery.status = 'DELIVERED';
        delivery.response = {
          statusCode: response.status,
          body: await response.text(),
          headers: this.responseHeadersToObject(response.headers)
        };
        
        webhook.successCount++;
        webhook.lastTriggered = new Date();
        this.updateWebhook(webhook.id, { 
          successCount: webhook.successCount,
          lastTriggered: webhook.lastTriggered 
        });
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      delivery.error = error instanceof Error ? error.message : 'Unknown error';
      
      if (delivery.attempts >= webhook.retryPolicy.maxRetries) {
        delivery.status = 'FAILED';
        webhook.failureCount++;
        this.updateWebhook(webhook.id, { failureCount: webhook.failureCount });
      } else {
        delivery.status = 'RETRYING';
        delivery.nextRetry = new Date(
          Date.now() + 
          webhook.retryPolicy.retryDelay * 
          Math.pow(webhook.retryPolicy.backoffMultiplier, delivery.attempts - 1)
        );
      }
    }

    this.updateDelivery(delivery);
  }

  private async sendWebhook(webhook: WebhookEndpoint, payload: WebhookPayload): Promise<Response> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      'X-Webhook-Signature': payload.signature,
      'X-Webhook-Event': payload.event,
      'X-Webhook-ID': payload.id,
      'User-Agent': 'TaskBridge-Webhooks/1.0',
      ...webhook.headers
    };

    // In a real application, this would be an actual HTTP request
    // For demo purposes, we'll simulate the request
    return this.simulateWebhookRequest(webhook.url, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload)
    });
  }

  private async simulateWebhookRequest(url: string, options: RequestInit): Promise<Response> {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, Math.random() * 1000 + 500));

    // Simulate different response scenarios
    const scenarios = [
      { probability: 0.8, status: 200, statusText: 'OK' },
      { probability: 0.1, status: 500, statusText: 'Internal Server Error' },
      { probability: 0.05, status: 404, statusText: 'Not Found' },
      { probability: 0.05, status: 408, statusText: 'Request Timeout' }
    ];

    const random = Math.random();
    let cumulative = 0;
    
    for (const scenario of scenarios) {
      cumulative += scenario.probability;
      if (random <= cumulative) {
        return new Response(
          JSON.stringify({ received: true, timestamp: new Date().toISOString() }),
          {
            status: scenario.status,
            statusText: scenario.statusText,
            headers: {
              'Content-Type': 'application/json',
              'X-Response-Time': `${Math.random() * 100 + 50}ms`
            }
          }
        );
      }
    }

    // Fallback
    return new Response('OK', { status: 200 });
  }

  // Webhook Testing
  async testWebhook(webhookId: string): Promise<{
    success: boolean;
    response?: any;
    error?: string;
    latency: number;
  }> {
    const webhook = this.getWebhook(webhookId);
    if (!webhook) {
      return {
        success: false,
        error: 'Webhook not found',
        latency: 0
      };
    }

    const testPayload: WebhookPayload = {
      id: `test_${Date.now()}`,
      event: 'job.created',
      timestamp: new Date(),
      data: {
        test: true,
        message: 'This is a test webhook delivery'
      },
      signature: ''
    };

    testPayload.signature = this.generateSignature(testPayload, webhook.secret);

    const startTime = Date.now();
    
    try {
      const response = await this.sendWebhook(webhook, testPayload);
      const latency = Date.now() - startTime;
      
      return {
        success: response.ok,
        response: {
          status: response.status,
          statusText: response.statusText,
          body: await response.text()
        },
        latency
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        latency: Date.now() - startTime
      };
    }
  }

  // Delivery History and Analytics
  getDeliveryHistory(webhookId?: string, limit: number = 50): WebhookDelivery[] {
    const deliveries = this.getDeliveries();
    
    let filtered = webhookId ? 
      deliveries.filter(d => d.endpointId === webhookId) : 
      deliveries;
    
    return filtered
      .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
      .slice(0, limit);
  }

  getWebhookStats(webhookId: string): {
    totalDeliveries: number;
    successRate: number;
    averageLatency: number;
    recentFailures: number;
    lastDelivery?: Date;
  } {
    const webhook = this.getWebhook(webhookId);
    if (!webhook) {
      return {
        totalDeliveries: 0,
        successRate: 0,
        averageLatency: 0,
        recentFailures: 0
      };
    }

    const deliveries = this.getDeliveryHistory(webhookId);
    const recentDeliveries = deliveries.filter(d => 
      d.createdAt.getTime() > Date.now() - 24 * 60 * 60 * 1000 // Last 24 hours
    );

    const successfulDeliveries = deliveries.filter(d => d.status === 'DELIVERED');
    const recentFailures = recentDeliveries.filter(d => d.status === 'FAILED').length;

    return {
      totalDeliveries: deliveries.length,
      successRate: deliveries.length > 0 ? (successfulDeliveries.length / deliveries.length) * 100 : 0,
      averageLatency: 0, // Would calculate from actual response times
      recentFailures,
      lastDelivery: webhook.lastTriggered
    };
  }

  // Signature Generation and Verification
  private generateSignature(payload: WebhookPayload, secret: string): string {
    // In a real application, this would use HMAC-SHA256
    // For demo purposes, we'll use a simple hash
    const data = JSON.stringify(payload);
    return `sha256=${this.simpleHash(data + secret)}`;
  }

  verifySignature(payload: string, signature: string, secret: string): boolean {
    const expectedSignature = `sha256=${this.simpleHash(payload + secret)}`;
    return signature === expectedSignature;
  }

  private simpleHash(input: string): string {
    let hash = 0;
    for (let i = 0; i < input.length; i++) {
      const char = input.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(16);
  }

  private generateSecret(): string {
    return Array.from(crypto.getRandomValues(new Uint8Array(32)))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  }

  // Utility Methods
  private responseHeadersToObject(headers: Headers): { [key: string]: string } {
    const obj: { [key: string]: string } = {};
    headers.forEach((value, key) => {
      obj[key] = value;
    });
    return obj;
  }

  private startDeliveryProcessor(): void {
    // Process delivery queue every 30 seconds
    setInterval(() => {
      this.processDeliveryQueue();
    }, 30000);
  }

  // Storage Methods
  private saveWebhooks(webhooks: WebhookEndpoint[]): void {
    localStorage.setItem(`${this.STORAGE_KEY}_endpoints`, JSON.stringify(webhooks));
  }

  private getDeliveries(): WebhookDelivery[] {
    const stored = localStorage.getItem(`${this.STORAGE_KEY}_deliveries`);
    return stored ? JSON.parse(stored) : [];
  }

  private saveDelivery(delivery: WebhookDelivery): void {
    const deliveries = this.getDeliveries();
    deliveries.push(delivery);
    
    // Keep only last 1000 deliveries
    if (deliveries.length > 1000) {
      deliveries.splice(0, deliveries.length - 1000);
    }
    
    localStorage.setItem(`${this.STORAGE_KEY}_deliveries`, JSON.stringify(deliveries));
  }

  private updateDelivery(delivery: WebhookDelivery): void {
    const deliveries = this.getDeliveries();
    const index = deliveries.findIndex(d => d.id === delivery.id);
    
    if (index !== -1) {
      deliveries[index] = delivery;
      localStorage.setItem(`${this.STORAGE_KEY}_deliveries`, JSON.stringify(deliveries));
    }
    
    // Update in-memory queue
    const queueIndex = this.deliveryQueue.findIndex(d => d.id === delivery.id);
    if (queueIndex !== -1) {
      this.deliveryQueue[queueIndex] = delivery;
    }
  }

  // Event Helpers - These would be called from other services
  static async jobCreated(jobData: any): Promise<void> {
    await webhookService.triggerEvent('job.created', jobData);
  }

  static async jobUpdated(jobData: any): Promise<void> {
    await webhookService.triggerEvent('job.updated', jobData);
  }

  static async quoteSubmitted(quoteData: any): Promise<void> {
    await webhookService.triggerEvent('quote.submitted', quoteData);
  }

  static async bookingConfirmed(bookingData: any): Promise<void> {
    await webhookService.triggerEvent('booking.confirmed', bookingData);
  }

  static async reviewSubmitted(reviewData: any): Promise<void> {
    await webhookService.triggerEvent('review.submitted', reviewData);
  }
}

export const webhookService = new WebhookService();
export type { WebhookEndpoint, WebhookEvent, WebhookPayload, WebhookDelivery };